/** Aligned with recycler appetite API — see recycler-appatite-feature.md */

export type RecyclerAppetiteRepeat = 'weekly' | 'monthly';

export const RECYCLER_APPETITE_UNITS = [
  'kg',
  'tons',
  'lbs',
  'bags',
  'cartons',
  'bottles',
  'pallets',
  'cubic_yards',
  'cubic_meters',
  'liters',
  'gallons',
] as const;

export type RecyclerAppetiteAmountUnit = (typeof RECYCLER_APPETITE_UNITS)[number];

/** A target is a mass. The backend refuses count units; older stored lines may
 *  still carry one and are shown flagged until re-entered. */
export const RECYCLER_APPETITE_MASS_UNITS = ['kg', 'tons', 'lbs'] as const;
export type RecyclerAppetiteMassUnit = (typeof RECYCLER_APPETITE_MASS_UNITS)[number];

export function isMassUnit(unit: string): unit is RecyclerAppetiteMassUnit {
  return (RECYCLER_APPETITE_MASS_UNITS as readonly string[]).includes(unit);
}

export interface RecyclerAppetiteLineInput {
  material_id: string;
  parent_category: string;
  quantity: number;
  unit: string;
}

/** Stored org JSON; legacy `weekdays` may exist and is ignored on PATCH. */
export interface RecyclerAppetiteLineStored extends RecyclerAppetiteLineInput {
  weekdays?: string[];
}

export interface RecyclerAppetiteStored {
  accepts_additional_quantity?: boolean;
  repeat?: RecyclerAppetiteRepeat;
  lines?: RecyclerAppetiteLineStored[];
}

export interface RecyclerAppetitePatchRequest {
  accepts_additional_quantity?: boolean;
  repeat?: RecyclerAppetiteRepeat;
  lines?: RecyclerAppetiteLineInput[];
}

export interface RecyclerAppetiteFulfillmentLine {
  /** The material_types alias id the line references. */
  material_id: string;
  parent_category: string;
  material_type_name?: string | null;
  catalog_material_id?: string | null;
  category_code?: string | null;
  /** Unit the target was entered in; null for untargeted received lines. */
  unit?: string | null;
  /** False for mass received in the period with no target line. */
  has_target?: boolean;
  target_kg: number;
  received_kg: number;
  fulfillment_ratio: number | null;
  excess_kg: number;
  is_over_target: boolean;
  is_excess_disallowed: boolean;
}

export interface RecyclerAppetiteFulfillmentPeriod {
  period_start: string;
  period_end_exclusive: string;
  lines: RecyclerAppetiteFulfillmentLine[];
}

export interface RecyclerAppetiteFulfillmentResponse {
  /** Present on current API; treat missing as `"weekly"`. */
  repeat?: RecyclerAppetiteRepeat;
  accepts_additional_quantity: boolean;
  this_week: RecyclerAppetiteFulfillmentPeriod;
  this_month: RecyclerAppetiteFulfillmentPeriod;
}
